import React, { useState } from "react";
import { Col, Row, Card, Form, Table } from "react-bootstrap";
import { ReportIssue } from "../components/Sidebar/General/ReportIssue";
import { Volunteering } from "../components/Sidebar/General/Volunteering";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

import { DateDisplay } from "../components/Extra/DateDisplay";
import { ShareButtons } from "../components/Extra/ShareButtons";
import { Helmet } from "react-helmet";
import standingOrderMandate from '../assets/FQW-StandingOrderForm.pdf';
import { UrlFixer } from "../utils/utils";

import {
  StyledTitle,
  StyledListContainer,
  StyledListSection,
  ListItemText,
  PostMeta,
  StyledIntro,
  StyledBody,
  StyledSectionHeaderComponent,
  StyledMembershipCard,
  StyledBankDetails,
  StyledAddress
} from "../utils/common-styles";


const initialPayPalOptions = {
  "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
  currency: "GBP",
  intent: "capture",
};

const membershipLevels = [
  {
    id: 2,
    name: "Family Membership",
    price: "10.00",
    description: "Annual family membership",
  },
  {
    id: 1,
    name: "Individual Membership",
    price: "5.00",
    description: "Annual individual membership",
  },
  {
    id: 3,
    name: "Under 23",
    price: "3.00",
    description: "Annual concession membership",
  },
];

export const MembershipPage = (props) => {

  const [selectedMembership, setSelectedMembership] = useState(
    membershipLevels[0]
  );
  const [donation, setDonation] = useState(0);

  return (
    <>
      <Helmet>
        <title>Membership details - The Friends of Queen's Wood</title>
        <meta name="description" content="The Friends of Queen's Wood membership details" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Membership details - The Friends of Queen's Wood" />
        <meta property="og:description" content="The Friends of Queen's Wood membership details" />
        <meta property="og:url" content={`${window.location.origin}`} />
        <meta property="og:image" content={UrlFixer('/assets/og-large-profile.jpg')} />
        <meta name="twitter:title" content="Membership details - The Friends of Queen\'s Wood" />
        <meta name="twitter:description" content="The Friends of Queen\'s Wood membership details" />
        <meta name="twitter:image" content={UrlFixer('/assets/og-large-profile.jpg')} />
      </Helmet>
      <StyledSectionHeaderComponent title="Membership" />
      <StyledListSection>
        <StyledListContainer>
          <Row>
            <Col xs={12} md={9}>
              <p style={{ marginTop: "25px" }}>&nbsp;</p>

              <ListItemText>
                <StyledTitle>Become a Member</StyledTitle>
              </ListItemText>
              <PostMeta className="post-meta">
                <Row>
                  <Col xs={12} md={3}>
                    <i className="fas fa-calendar-alt"> </i>{" "}
                    <DateDisplay datetime="2025-03-20 00:00:00" />
                  </Col>
                  <Col xs={12} md={3}>
                    <i className="fas fa-tags"></i>{" "}
                    <ul className="tag-list">
                      <li>Membership</li>
                    </ul>
                  </Col>
                  <Col xs={12} md={3}>
                    <ShareButtons
                      title={"Become a member of The Friends of Queen's Wood"}
                      shareUrl={window.location.href}
                    />
                  </Col>
                </Row>
              </PostMeta>

              <StyledIntro>
                You can support the aims of the Friends and help to preserve the
                wood by becoming a member. Membership entitles you to a regular
                Newsletter, talks, led walks in the Wood, special children's
                events and information about local conservation and
                environmental issues.
              </StyledIntro>
              <StyledBody>
                <p>
                  Membership runs for a full calendar year and costs £10 per annum for a family, £5 per annum for an individual and £3 per annum for those under the age of 23.
                </p>
                <p>
                  We offer a variety of ways to apply for and pay for
                  membership, including online via our PayPal checkout, by bank
                  transfer, standing order or by post.
                </p>
                <Row>
                  <Col md={12} className="mx-auto">
                    <StyledMembershipCard>
                      <Card.Header>
                        Pay online by PayPal account or by credit or debit card.
                      </Card.Header>
                      <Card.Body>
                        <Form.Group className="mb-4">
                          <Form.Label>
                            Please select a membership level
                          </Form.Label>
                          <Form.Select
                            value={selectedMembership.id}
                            onChange={(e) => {
                              const selected = membershipLevels.find(
                                (level) => level.id === parseInt(e.target.value)
                              );
                              setSelectedMembership(selected);
                            }}
                            style={{ width: '20rem' }}
                          >
                            {membershipLevels.map((level) => (
                              <option key={level.id} value={level.id}>
                                {level.name} - &pound;{level.price}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-4">
                          <Form.Label>
                            Would you like to add an optional donation? (&pound;)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="1"
                            value={donation || ""}
                            onChange={(e) => setDonation(parseFloat(e.target.value) || 0)}
                            placeholder="Enter amount"
                            style={{ width: '12rem' }}
                          />
                        </Form.Group>

                        <PayPalScriptProvider options={initialPayPalOptions}>
                          <PayPalButtons
                            style={{ layout: "vertical" }}
                            createOrder={(data, actions) => {
                              const total = (parseFloat(selectedMembership.price) + (donation || 0)).toFixed(2);
                              return actions.order.create({
                                purchase_units: [
                                  {
                                    amount: {
                                      value: total,
                                    },
                                    description: `${selectedMembership.name} - Friends of Queen's Wood${donation ? ` (includes £${donation.toFixed(2)} donation)` : ''}`,
                                  },
                                ],
                              });
                            }}
                            onApprove={(data, actions) => {
                              return actions.order.capture().then((details) => {
                                return true;
                              });
                            }}
                          />
                        </PayPalScriptProvider>
                      </Card.Body>
                    </StyledMembershipCard>
                  </Col>
                </Row>

                <Row>
                  <Col md={12} className="mx-auto">
                    <StyledBankDetails>
                      <Card.Header>To pay by bank transfer</Card.Header>
                      <Card.Body>
                        <Table>
                          <tbody>
                            <tr>
                              <td>Bank</td>
                              <td>NatWest Bank plc</td>
                            </tr>
                            <tr>
                              <td>Account Name</td>
                              <td>The Friends of Queen's Wood</td>
                            </tr>
                            <tr>
                              <td>Account Number</td>
                              <td>80746489</td>
                            </tr>
                            <tr>
                              <td>Sort Code</td>
                              <td>60-08-23</td>
                            </tr>
                            <tr>
                              <td>Reference</td>
                              <td>Please use your name for the reference</td>
                            </tr>
                            <tr>
                              <td colSpan="2">
                                <p>
                                  <strong>Please note:</strong>If you choose to
                                  pay by bank transfer, please be sure to send an
                                  email to{" "}
                                  <a href="mailto:membership@fqw.org.uk">
                                    membership@fqw.org.uk
                                  </a>{" "}
                                  to notify us of your details so that we can
                                  match the payment.
                                </p>
                                <p>Alternatively, you can pay by standing order. You can download our <a href={standingOrderMandate} target="_blank" rel="noreferrer">Standing order form</a> here and post it to the address given on the form.</p>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </Card.Body>
                    </StyledBankDetails>
                  </Col>
                </Row>

                <Row>
                  <Col md={12} className="mx-auto">
                    <StyledMembershipCard>
                      <Card.Header>
                        To pay by post
                      </Card.Header>
                      <Card.Body>
                        <p>Please include a covering letter with your cheque or postal order for the relevant amount to:</p>
                        <StyledAddress>
                          Membership FQW, <br />
                          84 St James' Lane, <br />
                          London, <br />
                          N10 3RD
                        </StyledAddress>
                      </Card.Body>
                    </StyledMembershipCard>
                  </Col>
                </Row>


              </StyledBody>
            </Col>
            <Col xs={12} md={3}>
              <p style={{ marginTop: "25px" }}>&nbsp;</p>
              <ReportIssue />
              <Volunteering />
            </Col>
          </Row>
        </StyledListContainer>
      </StyledListSection>
    </>
  );
};
